import { useEffect, useState } from 'preact/hooks';
import { request, ApiError, type Session, describeError } from '../api/client';
import type { WireCapturePreview, DisplayPreview } from '../api/wire';
import { displayDate } from '../api/adapter';
import { newIdempotencyKey } from '../api/ids';
import { enqueue } from '../state/queue';
import { localPreview } from '../api/localPreview';
import { activeDestination } from '../state/destinations';

import { TokenPill } from '../components/entries/TokenPill.jsx';
import { GlyphMark } from '../components/entries/GlyphMark.jsx';
import type { GlyphState } from '../components/entries/GlyphMark';
import type { EntryKind } from '../api/wire';
import { shortMonthLabel } from '../api/future';

/** Kind chooses the glyph. This is the only mapping capture needs, because a
 *  new entry has no state yet — `glyphStateFor` in the adapter covers the
 *  (kind, state) pairs that existing entries carry. */
const KIND_GLYPH: Record<EntryKind, GlyphState> = {
  task: 'open',
  note: 'note',
  question: 'question',
};

const MARKERS: Record<string, EntryKind> = { '.': 'task', '-': 'note', '?': 'question' };

type Normalized =
  | { ok: true; text: string; kind: EntryKind }
  | { ok: false; reason: string };

/** What to send, given what was typed.
 *
 *  `parse_capture` requires a marker *and a space* — `". "`, `"- "`, `"? "` —
 *  and answers 422 `unknown_symbol` for anything else. Requiring the user to
 *  type punctuation exactly defeats the two-tap goal, and `.buy milk` failing
 *  while `. buy milk` works is not a distinction worth teaching anyone.
 *
 *  So: a leading marker is accepted with or without its space, and text with no
 *  marker at all becomes a task, because on a phone that is what a capture
 *  almost always is. The resolved kind is always shown, so the guess is never
 *  hidden.
 */
function normalize(raw: string): Normalized | null {
  const trimmed = raw.trimStart();
  if (!trimmed) return null;

  const head = trimmed[0] ?? '';
  if (head === 'o' || head === '\u25cb') {
    const rest = trimmed.slice(1);
    if (rest === '' || rest.startsWith(' ')) {
      // The API rejects event capture outright: the calendar owns events and
      // this client never writes to it.
      return { ok: false, reason: 'Events live in your calendar, not the journal.' };
    }
  }

  const kind = MARKERS[head];
  if (kind) {
    const rest = trimmed.slice(1).trimStart();
    if (!rest) return null;
    return { ok: true, text: `${head} ${rest}`, kind };
  }
  return { ok: true, text: `. ${trimmed}`, kind: 'task' };
}

/** How long a capture waits for the server before it is queued instead. Long
 *  enough for a slow network, short enough that nobody gives up first. */
const CAPTURE_TIMEOUT_MS = 15_000;

/** The hero flow: thought to committed entry.
 *
 *  `/v1/capture/preview` takes no Idempotency-Key and is a pure function of its
 *  input, so it is safe to call on every debounced keystroke. It rejects text
 *  without a recognised `. ` / `- ` / `? ` prefix, which is an ordinary state
 *  while someone is still typing — so that rejection is swallowed rather than
 *  shown as an error.
 *
 *  The date shown in the preview is the date sent on commit. Pinning it here
 *  rather than letting the server resolve "today" at write time is what makes a
 *  capture composed at 23:58 land on the day it was written for.
 */
export function Capture({
  session,
  journalDate,
  open,
  into = 'day',
  fieldRef,
  prefill,
  onCaptured,
  onQueued,
  onCancel,
}: {
  session: Session;
  /** The journal day this capture is composed for. Sent explicitly so the entry
   *  lands on the day it was written for, not the day it happened to be sent.
   *  Without it the server places a dateless task in the month inbox instead. */
  journalDate: string | null;
  /** Kept mounted when closed. iOS Safari only raises the keyboard when
   *  `focus()` runs synchronously inside the tap gesture, and the element has to
   *  already exist for that to be possible — so this hides rather than unmounts.
   *  `display: none` would not work either: a non-rendered element cannot take
   *  focus. */
  open: boolean;
  /** Where this capture lands, decided by the page that opened it. The month
   *  inbox face files into its month and offers nothing else — the same rule the
   *  terminal client uses, where the month log's tasks page sets a month target
   *  and no day. */
  into?: 'day' | 'month';
  /** Owned by App so the capture bar's tap handler can focus this field during
   *  the gesture that opened the sheet. */
  fieldRef: { current: HTMLInputElement | null };
  /** Text the field starts with, e.g. `/Product` on that stream's filtered
   *  list — the frame's `capture into /Product`. Put in the field rather than
   *  applied invisibly, so what will be captured is what is on screen and the
   *  user can edit or delete it. */
  prefill?: string | null;
  onCaptured: (summary: string) => void;
  /** Kept locally because the network was not there. The text is never lost. */
  onQueued: (summary: string) => void;
  onCancel: () => void;
}) {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<DisplayPreview | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const field = fieldRef;

  useEffect(() => {
    if (open) {
      // Belt and braces for desktop and Android; iOS relies on the synchronous
      // focus in the capture bar's tap handler.
      field.current?.focus();
    } else {
      // Seeded while closed rather than on open, so opening never writes to the
      // field. Writing on open raced anything that typed into it immediately
      // after the tap — and it was redundant, since closing already resets.
      setText(prefill ? `${prefill} ` : '');
      setPreview(null);
      setError(null);
      // Staying mounted means `sending` outlives the capture that set it. Left
      // alone, the next sheet opened on a disabled `SAVING` and every commit
      // returned early — one capture saved, the one after it never did.
      setSending(false);
    }
  }, [open, field, prefill]);

  const normalized = normalize(text);

  useEffect(() => {
    if (!normalized || normalized.ok === false) {
      setPreview(null);
      setError(normalized && normalized.ok === false ? normalized.reason : null);
      return;
    }
    const sending = normalized.text;
    const kindOf = normalized.kind;

    // Offline is known up front; do not spend a doomed request to learn it. Nor
    // when there is no token — the parse is the same either way.
    if (!navigator.onLine || !session.token.trim()) {
      setPreview(localPreview(sending, kindOf, journalDate));
      setError(null);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const body = (
          await request<WireCapturePreview>(session, '/v1/capture/preview', {
            method: 'POST',
            body: journalDate ? { text: sending, date: journalDate } : { text: sending },
          })
        ).body;
        setPreview(body);
        setError(null);
      } catch (caught) {
        // A transport failure means offline, not invalid. Preview locally so the
        // capture button still works and the text can be queued — without this
        // the offline queue is unreachable.
        if (!(caught instanceof ApiError)) {
          setPreview(localPreview(sending, kindOf, journalDate));
          setError(null);
          return;
        }
        setPreview(null);
        // Normalization means a 422 here is a real refusal, not "still typing",
        // so it is shown rather than swallowed.
        setError(describeError(caught));
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [normalized?.ok === true ? normalized.text : null, session, journalDate]);

  /** Where a capture lands. The reference sheet pairs `LOG TO TODAY` with a
   *  `MONTH` button, and the server takes `date` or `month` on `/v1/capture`
   *  under the same scope — so the month inbox was reachable all along and this
   *  client only ever wrote to the day. */
  async function commit(event: Event, target: 'day' | 'month' = into) {
    event.preventDefault();
    if (!preview || sending) return;
    // Belt as well as braces: the button is disabled, and Enter also submits.
    if (target === 'month' && preview.kind !== 'task') return;
    setSending(true);
    const month = (preview.resolved_date ?? journalDate ?? '').slice(0, 7) || null;
    const payload = {
      text: normalized && normalized.ok ? normalized.text : text,
      // Exactly one of the two, always sent explicitly. Never left for the
      // server to resolve at replay time.
      date: target === 'month' ? null : preview.resolved_date,
      month: target === 'month' ? month : preview.resolved_month,
    };
    const destination = activeDestination();

    // Offline is known up front, so do not spend a request to discover it. So is
    // having no credential: a 401 is not news when the token is empty.
    if ((!navigator.onLine || !session.token.trim()) && destination) {
      await enqueue(destination, payload, preview.content);
      onQueued(preview.content);
      return;
    }

    // Minted here, once, for this logical capture, and carried into the queue if
    // the send fails — so a retry can never become a duplicate.
    const idempotencyKey = newIdempotencyKey();
    try {
      await request(session, '/v1/capture', {
        method: 'POST',
        idempotencyKey,
        // The previewed date, sent explicitly. Never left to the server.
        body: payload,
        // iOS can leave a fetch pending forever — after a suspend, a fold, a
        // network handover — and with no deadline the sheet sat on SAVING with
        // no way forward. A timeout is a transport failure like any other: the
        // text goes to the queue below.
        signal: AbortSignal.timeout(CAPTURE_TIMEOUT_MS),
      });
      onCaptured(preview.content);
    } catch (caught) {
      // A transport failure is not a reason to lose what someone wrote. A
      // refusal by the server is, because replaying it would fail identically.
      const refused = caught instanceof ApiError && caught.status >= 400 && caught.status < 500;
      if (!refused && destination) {
        await enqueue(destination, payload, preview.content, idempotencyKey);
        onQueued(preview.content);
        return;
      }
      setError(describeError(caught));
      setSending(false);
    }
  }

  const placedOn = preview?.resolved_date
    ? displayDate(preview.resolved_date)
    : preview?.resolved_month ?? null;
  const monthTarget = (preview?.resolved_date ?? journalDate ?? '').slice(0, 7);
  /** The month inbox is a list of tasks, so a note or a question filed there
   *  would be stored and never shown. The terminal client refuses `-` and `?`
   *  on the month log for exactly that reason; this says so rather than
   *  swallowing them. */
  const wrongKindForMonth =
    into === 'month' && preview !== null && preview.kind !== 'task' ? preview.kind : null;

  return (
    <form
      onSubmit={(event) => void commit(event, 'day')}
      // The field takes focus at the start of the slide, so iOS measures the
      // caret mid-animation. Setting the selection again once the sheet has
      // arrived makes it measure again, in the right place.
      onTransitionEnd={(event) => {
        const input = field.current;
        if (!open || event.target !== event.currentTarget || event.propertyName !== 'transform') return;
        if (input && document.activeElement === input) {
          const { selectionStart, selectionEnd } = input;
          input.setSelectionRange(selectionStart, selectionEnd);
        }
      }}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--bujo-surface-page)',
        display: 'flex',
        flexDirection: 'column',
        padding: 'var(--bujo-gutter)',
        paddingTop: 'calc(var(--bujo-gutter) + env(safe-area-inset-top))',
        // The sides too: in landscape on the iPhone Duo the clock sits in the
        // top-right corner, over CANCEL, unless the sheet keeps clear of it.
        paddingLeft: 'calc(var(--bujo-gutter) + env(safe-area-inset-left))',
        paddingRight: 'calc(var(--bujo-gutter) + env(safe-area-inset-right))',
        gap: 'var(--bujo-space-4)',
        zIndex: 30,
        // Hidden by opacity and position, never by `visibility` or `display`:
        // both of those make the field unfocusable, which defeats the whole
        // point of keeping it mounted. `pointerEvents: none` stops the offscreen
        // sheet swallowing taps, and tabIndex -1 keeps it out of the tab order
        // while still allowing the programmatic focus the tap handler does.
        opacity: open ? 1 : 0,
        pointerEvents: open ? 'auto' : 'none',
        // `none`, not `translateY(0)`, once open: iOS WebKit places the caret
        // wrongly inside a transformed ancestor, and an identity transform is
        // still a transform — the caret sat ~15px below the text on the Duo,
        // where the slide had nearly arrived when the field took focus.
        transform: open ? 'none' : 'translateY(100%)',
        transition:
          'transform var(--bujo-dur-sheet) var(--bujo-ease), opacity var(--bujo-dur-sheet) var(--bujo-ease)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
        <span style={labelStyle}>CAPTURE</span>
        <button type="button" onClick={onCancel} style={{ ...labelStyle, color: 'var(--bujo-text-meta)', cursor: 'pointer', minHeight: 'var(--bujo-row-min)' }}>
          CANCEL
        </button>
      </div>

      <div style={{ display: 'flex', gap: 'var(--bujo-space-2)', alignItems: 'center' }}>
        <GlyphMark state={preview ? KIND_GLYPH[preview.kind] ?? 'open' : 'open'} size="capture" priority={preview?.priority ?? false} />
        <input
          ref={field}
          value={text}
          onInput={(e) => setText((e.target as HTMLInputElement).value)}
          placeholder=". task   - note   ? question"
          autocomplete="off"
          autocapitalize="sentences"
          spellcheck
          enterkeyhint="done"
          tabIndex={open ? 0 : -1}
          style={{
            flex: 1,
            minHeight: 'var(--bujo-tap-min)',
            background: 'transparent',
            border: 0,
            // Accent, not hairline: this is the focused field for the whole
            // life of the sheet, and the reference draws it that way.
            borderBottom: '1px solid var(--bujo-accent)',
            color: 'var(--bujo-text-primary)',
            font: 'var(--bujo-fw-regular) var(--bujo-fs-sheet)/normal var(--bujo-font-mono)',
            borderRadius: 0,
          }}
        />
      </div>

      <div style={{ minHeight: 96, borderTop: '1px solid var(--bujo-rule-hairline)', paddingTop: 'var(--bujo-space-3)' }}>
        {/* The reference names this region. Without the label the parsed result
            reads as loose text under the field rather than the sheet's answer. */}
        <div style={{ ...labelStyle, color: 'var(--bujo-text-meta)', paddingBottom: 'var(--bujo-space-3)' }}>
          PARSED
        </div>
        {preview ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--bujo-space-2)' }}>
            <span style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-entry)/var(--bujo-lh-row) var(--bujo-font-mono)', color: 'var(--bujo-text-primary)' }}>
              {preview.content}
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--bujo-space-2)', alignItems: 'baseline' }}>
              {preview.stream ? <TokenPill bordered kind="stream" name={preview.stream} /> : null}
              {preview.project ? <TokenPill bordered kind="project" name={preview.project} /> : null}
              {preview.contexts.map((name) => (
                <TokenPill bordered key={name} kind="context" name={name} />
              ))}
            </div>
            {placedOn ? (
              <span style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1 var(--bujo-font-mono)', color: 'var(--bujo-text-meta)' }}>
                {preview.kind.toUpperCase()} · {placedOn}
              </span>
            ) : null}
          </div>
        ) : (
          <span style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-micro)/1.6 var(--bujo-font-mono)', color: 'var(--bujo-text-meta)' }}>
            {/* Not `Reading that…` once the read has failed. This slot ignored
                `error` entirely, so a refused or unreachable preview left the
                sheet claiming to still be thinking, with the failure printed
                underneath it. The error line below is the message; this stays
                quiet. */}
            {error
              ? ''
              : text.trim()
                ? 'Reading that\u2026'
                : 'Just type \u2014 it becomes a task. Lead with - for a note or ? for a question. Add /stream #project @context to tag it.'}
          </span>
        )}
      </div>

      {error ? (
        <span style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.4 var(--bujo-font-mono)', color: 'var(--bujo-state-error)' }}>
          ! {error}
        </span>
      ) : wrongKindForMonth ? (
        <span style={{ font: 'var(--bujo-fw-regular) var(--bujo-fs-meta)/1.4 var(--bujo-font-mono)', color: 'var(--bujo-state-error)' }}>
          ! The month inbox holds tasks. A {wrongKindForMonth} needs a day — capture it
          from the daily log.
        </span>
      ) : null}

      {/* Two destinations, as the reference sheet draws them: the day as the
          primary action, the month inbox beside it. Each commits directly — no
          hidden mode — so what the button says is where the entry lands. */}
      <div
        style={{
          marginTop: 'auto',
          paddingBottom: 'env(safe-area-inset-bottom)',
          display: 'flex',
          gap: 'var(--bujo-space-3)',
        }}
      >
        <button
          type="submit"
          disabled={!preview || sending || Boolean(wrongKindForMonth)}
          style={commitButton(Boolean(preview) && !wrongKindForMonth, true)}
        >
          {sending
            ? 'SAVING'
            : into === 'month'
              ? `LOG TO ${shortMonthLabel(monthTarget)}`
              : placedOn && !preview?.resolved_month
                ? 'LOG TO TODAY'
                : 'LOG IT'}
        </button>
        {/* No day option from the month inbox: that face holds month tasks, and
            offering a day there would file somewhere the page cannot show. */}
        {into === 'month' ? null : (
          <button
            type="button"
            disabled={!preview || sending || !monthTarget}
            onClick={(event) => void commit(event, 'month')}
            aria-label={monthTarget ? `Log into the ${monthTarget} month inbox instead` : 'Month inbox'}
            style={{ ...commitButton(Boolean(preview) && Boolean(monthTarget), false), flex: 'none', padding: '0 var(--bujo-space-4)' }}
          >
            MONTH
          </button>
        )}
      </div>
    </form>
  );
}

/** The sheet's commit buttons. Primary carries the accent; the secondary sits
 *  on a hairline, which is how the reference distinguishes them. */
function commitButton(enabled: boolean, primary: boolean) {
  return {
    flex: primary ? 1 : undefined,
    minHeight: 'var(--bujo-tap-min)',
    border: `1px solid ${enabled && primary ? 'var(--bujo-accent)' : 'var(--bujo-rule-hairline)'}`,
    background: 'none',
    color: enabled
      ? primary
        ? 'var(--bujo-accent)'
        : 'var(--bujo-text-meta)'
      : 'var(--bujo-text-faint)',
    font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
    letterSpacing: 'var(--bujo-ls-label)',
    cursor: enabled ? 'pointer' : 'default',
  } as const;
}

const labelStyle = {
  font: 'var(--bujo-fw-bold) var(--bujo-fs-label)/1 var(--bujo-font-mono)',
  letterSpacing: 'var(--bujo-ls-label)',
  color: 'var(--bujo-label)',
};
